import type { EnvironmentProviders } from '@angular/core';
import { isDevMode } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, RouterState } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import {
  socialLoginByCode,
  startLoadRequiredData,
} from './effects/auth.effects';
import {
  loadActiveOrganization,
  loadMyOrganizations,
} from './effects/organization.effect';
import { getPermissions$ } from './effects/permissions.effects';
import {
  beforeLoadProjects,
  closeProject,
  createProject,
  deleteProject,
  getProject,
  loadProjects,
  requestDeleteProject,
  updateProject,
} from './effects/project.effects';
import {
  createProjectContact,
  createProjectContacts,
  deleteProjectContact,
  loadProjectContacts,
  updateProjectContact,
} from './effects/project-contact.effects';
import {
  createProjectLocation,
  deleteProjectLocation,
  loadProjectLocations,
  requestDeleteProjectLocation,
  updateProjectLocation,
} from './effects/project-location.effects';
import {
  createProjectLocationPlan,
  loadProjectLocationPlans,
  loadSelectedPlan,
  requestDeletePlan,
  startCopyPlan,
  startDeletePlan,
  editProjectLocationPlan,
  uploadProjectLocationPlanFloorImage,
  downloadPlanPicture,
} from './effects/project-location-plan.effects';
import {
  loadCoreTypes,
  loadPlanCores,
  saveCore,
  loadProjectLocationPlanCores,
  updateProjectLocationPlanCores,
  updateCore,
  copyCore,
  deleteCore,
  bulkDeleteCores,
  bulkCreateCores,
} from './effects/project-location-plan-core.effects';
import {
  loadProjectLocationPlanCameras,
  updateProjectLocationPlanCamera,
  updateProjectLocationPlanCameras,
  savePlanProjectCamera,
  loadPlanProjectCameras,
  copyCamera,
  deleteCamera,
  bulkDeleteProjectLocationPlanCameras,
  bulkCreateCameras,
} from './effects/project-location-plan-camera.effects';
import {
  bulkCreateCameraAddons,
  bulkDeleteCameraAddons,
  bulkDeletePlanAddons,
  bulkUpdateCameraAddons,
  bulkUpdatePlanAddons,
  createProjectAddon,
  loadAddonTypes,
  loadProjectCameraAddons,
  loadProjectPlanAddons,
  updateProjectAddon,
} from './effects/project-addon.effects';
import { getMe, setInterfaceLanguage } from './effects/user-settings.effect';
import {
  getUserProfile,
  logout,
  redirectAfterProfileLoaded,
  setStoreSession,
} from './effects/user.effects';
import { organizationFeature } from './state/organization/organization.feature';
import { permissionsFeature } from './state/permissions/permissions.feature';
import { projectContactFeature } from './state/project-contact/project-contact.feature';
import { projectFeature } from './state/project/project.feature';
import { projectLocationFeature } from './state/project-location/project-location.feature';
import { projectLocationPlanFeature } from './state/project-location-plan/project-location-plan.feature';
import { projectLocationPlanCoreFeature } from './state/project-location-plan-core/project-location-plan-core.feature';
import { projectLocationPlanCameraFeature } from './state/project-location-plan-camera/project-location-plan-camera.feature';
import { projectAddonFeature } from './state/project-addon/project-addon.feature';
import { userSettingsFeature } from './state/user-settings/user-settings.feature';
import { userFeature } from './state/user/user.feature';
import { projectExternalCameraFeature } from './state/plan-external-camera/project-external-camera.feature';
import {
  filterTrigger,
  loadExternalCameraBrands,
  loadExternalCameraTypes,
  loadFilteredExternalCameras,
} from './effects/project-external-camera.effects';

export const provideAppState = (): EnvironmentProviders[] => [
  provideStore({}),
  provideState(userFeature),
  provideState(userSettingsFeature),
  provideState(organizationFeature),
  provideState(projectFeature),
  provideState(projectContactFeature),
  provideState(projectLocationFeature),
  provideState(projectLocationPlanFeature),
  provideState(projectLocationPlanCoreFeature),
  provideState(projectLocationPlanCameraFeature),
  provideState(projectAddonFeature),
  provideState(permissionsFeature),
  provideState(projectExternalCameraFeature),
  provideEffects({
    loadExternalCameraBrands,
    loadFilteredExternalCameras,
    loadExternalCameraTypes,
    filterTrigger,
  }),
  provideEffects({
    socialLoginByCode,
    startLoadRequiredData,
  }),
  provideEffects({
    getUserProfile,
    logout,
    redirectAfterProfileLoaded,
    setStoreSession,
  }),
  provideEffects({
    getMe,
    setInterfaceLanguage,
  }),
  provideEffects({
    loadActiveOrganization,
    loadMyOrganizations,
  }),
  provideEffects({
    beforeLoadProjects,
    closeProject,
    createProject,
    requestDeleteProject,
    deleteProject,
    getProject,
    loadProjects,
    updateProject,
  }),
  provideEffects({
    createProjectContact,
    createProjectContacts,
    deleteProjectContact,
    loadProjectContacts,
    updateProjectContact,
  }),
  provideEffects({
    loadProjectLocations,
    createProjectLocation,
    updateProjectLocation,
    requestDeleteProjectLocation,
    deleteProjectLocation,
  }),
  provideEffects({
    createProjectLocationPlan,
    loadProjectLocationPlans,
    loadSelectedPlan,
    requestDeletePlan,
    startCopyPlan,
    editProjectLocationPlan,
    startDeletePlan,
    uploadProjectLocationPlanFloorImage,
    downloadPlanPicture,
  }),
  provideEffects({
    loadCoreTypes,
    loadProjectLocationPlanCores,
    updateProjectLocationPlanCores,
    loadPlanCores,
    saveCore,
    updateCore,
    copyCore,
    deleteCore,
    bulkDeleteCores,
    bulkCreateCores,
  }),
  provideEffects({
    loadProjectLocationPlanCameras,
    updateProjectLocationPlanCameras,
    updateProjectLocationPlanCamera,
    savePlanProjectCamera,
    loadPlanProjectCameras,
    copyCamera,
    deleteCamera,
    bulkDeleteProjectLocationPlanCameras,
    bulkCreateCameras,
  }),
  provideEffects({
    loadAddonTypes,
    loadProjectPlanAddons,
    loadProjectCameraAddons,
    createProjectAddon,
    updateProjectAddon,
    bulkCreateCameraAddons,
    bulkUpdateCameraAddons,
    bulkUpdatePlanAddons,
    bulkDeleteCameraAddons,
    bulkDeletePlanAddons,
  }),
  provideEffects({
    getPermissions$,
  }),
  provideRouterStore({
    stateKey: 'router',
    routerState: RouterState.Minimal,
  }),
  provideStoreDevtools({
    maxAge: 25,
    logOnly: !isDevMode(),
    autoPause: true,
    trace: false,
  }),
];
